/** MIME types the narrow admin FILE fields (widget forms) accept. */
export const ALLOWED_ADMIN_UPLOAD_MIME_TYPES: string[] = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
  "video/mp4",
  "video/webm",
  "video/quicktime",
];

/** Default `accept` for Form1 / FieldsFormatter / TableFormatter file inputs */
export const DEFAULT_ADMIN_FILE_INPUT_ACCEPT =
  ALLOWED_ADMIN_UPLOAD_MIME_TYPES.join(",");

/**
 * `accept` for the File Bucket, which stores arbitrary objects. The browser
 * dialog is unrestricted; BLOCKED_UPLOAD_EXTENSIONS below is what actually
 * rejects a file, and the server enforces the same list.
 */
export const ANY_FILE_INPUT_ACCEPT = "*/*";

/**
 * Size cap for File Bucket uploads. Higher than the 200MB every other admin
 * upload keeps, but still bounded: /api/upload buffers the whole file in server
 * memory before it reaches S3. Keep in sync with server/utils/fileUtils.js.
 */
export const MAX_BUCKET_UPLOAD_BYTES = 500 * 1024 * 1024; // 500MB

/**
 * Refused everywhere. Keep in sync with server/utils/fileUtils.js.
 *  - markup the CDN would serve inline (stored XSS on the asset domain)
 *  - executables / installers / server-side scripts we never want to host
 */
export const BLOCKED_UPLOAD_EXTENSIONS: string[] = [
  "html", "htm", "xhtml", "shtml", "svgz",
  "exe", "dll", "msi", "bat", "cmd", "com", "cpl", "scr", "pif",
  "vbs", "vbe", "ws", "wsf", "wsh", "ps1", "psm1", "sh", "bash", "zsh",
  "jar", "apk", "app", "dmg", "pkg", "deb", "rpm", "bin",
  "php", "phtml", "php3", "php4", "php5", "asp", "aspx", "jsp", "cgi",
];

/** Canonical file-type keys. Mirrors FILE_TYPES in server/utils/fileUtils.js. */
export const FILE_TYPE_KEYS = [
  "image",
  "video",
  "audio",
  "document",
  "spreadsheet",
  "presentation",
  "archive",
  "font",
  "code",
  "other",
] as const;

export type FileTypeKey = (typeof FILE_TYPE_KEYS)[number];

export interface FileTypeDescriptor {
  key: FileTypeKey;
  /** Human label for badges and filters */
  label: string;
  /** Tailwind classes for the type badge */
  badgeClassName: string;
  /** Whether the item can be shown as a thumbnail */
  isPreviewable: boolean;
}

export const FILE_TYPE_DESCRIPTORS: Record<FileTypeKey, FileTypeDescriptor> = {
  image: {
    key: "image",
    label: "Image",
    badgeClassName: "bg-blue-100 text-blue-700 border-blue-200",
    isPreviewable: true,
  },
  video: {
    key: "video",
    label: "Video",
    badgeClassName: "bg-pink-100 text-pink-700 border-pink-200",
    isPreviewable: true,
  },
  audio: {
    key: "audio",
    label: "Audio",
    badgeClassName: "bg-teal-100 text-teal-700 border-teal-200",
    isPreviewable: false,
  },
  document: {
    key: "document",
    label: "Document",
    badgeClassName: "bg-red-100 text-red-700 border-red-200",
    isPreviewable: false,
  },
  spreadsheet: {
    key: "spreadsheet",
    label: "Spreadsheet",
    badgeClassName: "bg-green-100 text-green-700 border-green-200",
    isPreviewable: false,
  },
  presentation: {
    key: "presentation",
    label: "Presentation",
    badgeClassName: "bg-orange-100 text-orange-700 border-orange-200",
    isPreviewable: false,
  },
  archive: {
    key: "archive",
    label: "Archive",
    badgeClassName: "bg-amber-100 text-amber-700 border-amber-200",
    isPreviewable: false,
  },
  font: {
    key: "font",
    label: "Font",
    badgeClassName: "bg-indigo-100 text-indigo-700 border-indigo-200",
    isPreviewable: false,
  },
  code: {
    key: "code",
    label: "Data / Code",
    badgeClassName: "bg-slate-100 text-slate-700 border-slate-200",
    isPreviewable: false,
  },
  other: {
    key: "other",
    label: "Other",
    badgeClassName: "bg-gray-100 text-gray-700 border-gray-200",
    isPreviewable: false,
  },
};

/** Extension -> file type. The extension wins over the browser-reported MIME. */
const EXTENSION_FILE_TYPES: Record<string, FileTypeKey> = {
  jpg: "image", jpeg: "image", png: "image", gif: "image", webp: "image",
  bmp: "image", svg: "image", ico: "image", avif: "image", tif: "image",
  tiff: "image", heic: "image",
  mp4: "video", webm: "video", mov: "video", avi: "video", mkv: "video",
  m4v: "video",
  mp3: "audio", wav: "audio", ogg: "audio", m4a: "audio", aac: "audio",
  flac: "audio",
  pdf: "document", doc: "document", docx: "document", odt: "document",
  rtf: "document", txt: "document", md: "document", epub: "document",
  xls: "spreadsheet", xlsx: "spreadsheet", ods: "spreadsheet",
  csv: "spreadsheet", tsv: "spreadsheet",
  ppt: "presentation", pptx: "presentation", odp: "presentation",
  zip: "archive", rar: "archive", "7z": "archive", tar: "archive",
  gz: "archive", tgz: "archive", bz2: "archive",
  ttf: "font", otf: "font", woff: "font", woff2: "font", eot: "font",
  json: "code", xml: "code", yml: "code", yaml: "code", css: "code",
  srt: "code", vtt: "code",
};

const MIME_PREFIX_FILE_TYPES: Record<string, FileTypeKey> = {
  image: "image",
  video: "video",
  audio: "audio",
  font: "font",
  text: "document",
};

/** Lowercased extension without the dot, from a file name or a URL. */
export function getFileExtension(nameOrUrl?: string | null): string {
  if (!nameOrUrl || typeof nameOrUrl !== "string") return "";
  const withoutQuery = nameOrUrl.split(/[?#]/)[0] ?? "";
  const base = withoutQuery.split("/").pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return "";
  return base.slice(dot + 1).toLowerCase();
}

/**
 * Resolve the canonical file type of a File or of a stored bucket URL.
 * Mirrors detectFileType in server/utils/fileUtils.js — the server derives and
 * persists `file_type`; this is the client-side fallback for rows written
 * before that column existed.
 */
export function detectFileType(
  nameOrUrl?: string | null,
  mimeType?: string | null
): FileTypeKey {
  const ext = getFileExtension(nameOrUrl);
  if (ext && EXTENSION_FILE_TYPES[ext]) return EXTENSION_FILE_TYPES[ext];

  if (mimeType) {
    const prefix = mimeType.split("/")[0] ?? "";
    if (MIME_PREFIX_FILE_TYPES[prefix]) return MIME_PREFIX_FILE_TYPES[prefix];
    if (mimeType === "application/pdf") return "document";
    if (mimeType.includes("zip") || mimeType.includes("compressed")) return "archive";
    if (mimeType.includes("json") || mimeType.includes("xml")) return "code";
  }

  return "other";
}

/** Descriptor for a stored row: trusts the persisted type, falls back to the URL. */
export function resolveFileTypeDescriptor(
  storedFileType?: string | null,
  url?: string | null
): FileTypeDescriptor {
  const stored = storedFileType as FileTypeKey | undefined;
  if (stored && FILE_TYPE_DESCRIPTORS[stored]) return FILE_TYPE_DESCRIPTORS[stored];
  return FILE_TYPE_DESCRIPTORS[detectFileType(url)];
}

export function isBlockedUploadExtension(fileName?: string | null): boolean {
  const ext = getFileExtension(fileName);
  return !!ext && BLOCKED_UPLOAD_EXTENSIONS.includes(ext);
}

/** "1.4 MB" / "812 KB" — used in upload size errors and bucket listings. */
export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}
